import { useCallback } from 'react';

import type { IntakeFormDefinition, UUID } from '@aline/common';
import { Apollo } from 'src/gql/generated';

/**
 * Create/update/remove the intake form template attached to a template workflow.
 * The mutations select `workflow.intakeFormTemplate` (or the template by id)
 * back, so Apollo's normalized cache refreshes `GetWorkflowPhases` automatically
 * — no manual cache writes needed.
 */
export function useIntakeFormTemplate(workflowId: UUID) {
  const [createTemplate] = Apollo.useCreateIntakeFormTemplateMutation();
  const [attachTemplate] = Apollo.useAttachIntakeFormTemplateMutation();
  const [updateTemplate] = Apollo.useUpdateIntakeFormTemplateMutation();
  const [detachTemplate, { loading: removing }] = Apollo.useDetachIntakeFormTemplateMutation();

  const saveIntakeForm = useCallback(
    async (params: {
      existingTemplateId: UUID | null;
      formDefinition: IntakeFormDefinition;
      isAttached: boolean;
      title: string;
      onTemplateCreated?: (intakeFormTemplateId: UUID) => void;
    }): Promise<UUID> => {
      const { existingTemplateId, formDefinition, isAttached, onTemplateCreated, title } = params;

      if (existingTemplateId) {
        await updateTemplate({
          variables: { input: { formDefinition, intakeFormTemplateId: existingTemplateId, title } },
        });
        /**
         * A prior create can commit the template but fail its follow-up attach
         * (they are separate mutations); re-attach here so the workflow ends up
         * linked rather than pointing at an orphaned, unattached template.
         */
        if (!isAttached)
          await attachTemplate({
            variables: { input: { intakeFormTemplateId: existingTemplateId, workflowId } },
          });
        return existingTemplateId;
      }

      const created = await createTemplate({ variables: { input: { formDefinition, title } } });
      const newTemplateId = created.data?.createIntakeFormTemplate.intakeFormTemplate.id;
      if (!newTemplateId) throw new Error('Failed to create intake form template.');
      /**
       * Report the new id before attaching: if the attach below fails, the caller
       * has already recorded it, so a retry updates + attaches this template
       * instead of creating a second (orphan) one.
       */
      onTemplateCreated?.(newTemplateId);

      await attachTemplate({
        variables: { input: { intakeFormTemplateId: newTemplateId, workflowId } },
      });
      return newTemplateId;
    },
    [attachTemplate, createTemplate, updateTemplate, workflowId]
  );

  const removeIntakeForm = useCallback(
    () => detachTemplate({ variables: { input: { workflowId } } }),
    [detachTemplate, workflowId]
  );

  /**
   * Link an existing reusable template to this workflow, replacing any currently
   * attached one. `replaceExisting` makes the swap a single atomic mutation: a
   * client-side detach-then-attach would lose the existing form if the attach
   * failed in between.
   */
  const linkIntakeForm = useCallback(
    (intakeFormTemplateId: UUID) =>
      attachTemplate({
        variables: { input: { intakeFormTemplateId, replaceExisting: true, workflowId } },
      }),
    [attachTemplate, workflowId]
  );

  const renameIntakeForm = useCallback(
    (intakeFormTemplateId: UUID, title: string) =>
      updateTemplate({ variables: { input: { intakeFormTemplateId, title } } }),
    [updateTemplate]
  );

  return { linkIntakeForm, removeIntakeForm, removing, renameIntakeForm, saveIntakeForm };
}
